import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Bell, Mail, CalendarPlus, Radio, X } from "lucide-react";
import { api } from "../../lib/api";

/**
 * Header bell — unified live notifications (new requests + unread messages).
 * Reuses the WS event bus owned by RequestsKanban. Clicking opens an inline
 * dropdown with the latest 10 inbox items.
 */
function fmtRel(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  if (diff < 1) return "az önce";
  if (diff < 60) return `${Math.round(diff)} dk önce`;
  if (diff < 1440) return `${Math.round(diff / 60)} sa önce`;
  return `${Math.round(diff / 1440)} g önce`;
}

export default function NotificationBell() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const ref = useRef(null);

  const { data: counts } = useQuery({
    queryKey: ["inbox", "counts"],
    queryFn: () => api.get("/admin/inbox/counts").then((r) => r.data),
    refetchInterval: 30000,
  });
  const { data: items = [] } = useQuery({
    queryKey: ["inbox", "bell"],
    queryFn: () => api.get("/admin/inbox", { params: { limit: 10 } }).then((r) => r.data),
    refetchInterval: open ? 15000 : 60000,
  });

  // Listen for live WS events to flash the bell and refresh.
  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail;
      if (!msg || msg.type !== "new") return;
      setPulse(true);
      setTimeout(() => setPulse(false), 4000);
      qc.invalidateQueries({ queryKey: ["inbox"] });
    };
    window.addEventListener("inbox:ws-event", handler);
    return () => window.removeEventListener("inbox:ws-event", handler);
  }, [qc]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const total = counts?.total ?? 0;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`relative p-2 rounded-full hover:bg-indigo-50 transition ${pulse ? "animate-pulse" : ""}`}
        data-testid="notif-bell"
        aria-label="Bildirimler"
      >
        <Bell size={18} className={pulse ? "text-amber-500" : "text-stone-600"} />
        {total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 border-2 border-white" data-testid="notif-badge">
            {total > 99 ? "99+" : total}
          </span>
        )}
        {pulse && <span className="absolute inset-0 rounded-full ring-2 ring-amber-400 animate-ping" />}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-indigo-100 overflow-hidden z-50" data-testid="notif-panel">
          <div className="px-4 py-3 flex items-center gap-2 text-white"
               style={{ background: "linear-gradient(135deg,#1e1b4b 0%,#4c1d95 100%)" }}>
            <Radio size={14} className="animate-pulse text-emerald-300" />
            <div className="flex-1">
              <div className="text-sm font-bold">Canlı Bildirimler</div>
              <div className="text-[11px] opacity-80">
                {counts?.pending_appointments ?? 0} talep · {counts?.unread_messages ?? 0} mesaj
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/20"><X size={14} /></button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto divide-y">
            {items.length === 0 && (
              <div className="text-center text-sm text-stone-400 italic p-8">Henüz bildirim yok.</div>
            )}
            {items.map((it) => {
              const Icon = it.kind === "message" ? Mail : CalendarPlus;
              const bg = it.kind === "message" ? "from-sky-500 to-sky-700" : "from-rose-500 to-rose-700";
              const to = it.kind === "message" ? "/admin/workspace/mesajlar" : "/admin/workspace/talepler";
              return (
                <Link
                  key={`${it.kind}:${it.id}`}
                  to={to}
                  onClick={() => setOpen(false)}
                  className="flex items-start gap-2.5 p-3 hover:bg-indigo-50/60 transition"
                  data-testid={`notif-item-${it.id}`}
                >
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${bg} text-white flex items-center justify-center shrink-0 shadow`}>
                    <Icon size={14} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate">{it.name}</p>
                    {it.subject && <p className="text-[11px] text-stone-700 truncate">{it.subject}</p>}
                    <p className="text-[11px] text-stone-500 line-clamp-2">{it.preview}</p>
                    <p className="text-[10px] text-stone-400 mt-0.5">{fmtRel(it.created_at)}</p>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="px-3 py-2 border-t bg-stone-50 flex items-center gap-2">
            <Link to="/admin/workspace/talepler" onClick={() => setOpen(false)} className="text-xs text-indigo-700 hover:underline font-semibold">
              Tüm talepler →
            </Link>
            <Link to="/admin/workspace/mesajlar" onClick={() => setOpen(false)} className="text-xs text-indigo-700 hover:underline font-semibold ml-auto">
              Tüm mesajlar →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
